import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const HelperPreferencesStep = ({ formData, onChange, errors }) => {
  const helperTypes = [
    {
      value: 'lived-experience',
      title: 'Lived-Experience Volunteer',
      description: 'Peer support from someone who has faced similar challenges',
      icon: 'Users',
      features: [
        'Personal experience with mental health challenges',
        'Empathetic peer-to-peer support',
        'Non-clinical guidance and understanding',
        'Usually available for immediate sessions'
      ]
    },
    {
      value: 'licensed-professional',
      title: 'Licensed Professional',
      description: 'Clinical support from qualified mental health providers',
      icon: 'Stethoscope',
      features: [
        'Licensed psychologists, therapists, or counselors',
        'Clinical assessment and treatment',
        'Evidence-based therapeutic approaches',
        'May require advance booking'
      ]
    },
    {
      value: 'no-preference',
      title: 'No Preference',
      description: 'Open to either type based on availability',
      icon: 'Shuffle',
      features: [
        'Fastest matching available',
        'Flexible based on your needs',
        'Access to broader helper pool',
        'Recommended for urgent support'
      ]
    }
  ];

  const locationOptions = [
    { value: 'main-campus', label: 'Main Campus' },
    { value: 'downtown-office', label: 'Downtown Office' },
    { value: 'north-building', label: 'North Building' },
    { value: 'south-building', label: 'South Building' },
    { value: 'remote', label: 'Remote/Virtual Only' },
    { value: 'any', label: 'Any Location' }
  ];

  const sessionFormatOptions = [
    { value: 'video', label: 'Video Call' },
    { value: 'audio', label: 'Audio Call Only' },
    { value: 'chat', label: 'Text Chat Only' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'in-person', label: 'In-Person (if available)' }
  ];

  const handleHelperTypeSelect = (value) => {
    onChange('helperType', value);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Helper Preferences
        </h3>
        <p className="text-sm text-muted-foreground">
          Choose the type of support that feels right for you
        </p>
      </div>
      <div className="space-y-3">
        <label className="text-sm font-medium text-foreground">
          Preferred Helper Type <span className="text-error">*</span>
        </label>
        {helperTypes?.map((type) => (
          <div
            key={type?.value}
            onClick={() => handleHelperTypeSelect(type?.value)}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              formData?.helperType === type?.value
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-card'
            }`}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={type?.icon} size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-foreground">{type?.title}</p>
                  {formData?.helperType === type?.value && (
                    <Icon name="CheckCircle2" size={20} className="text-primary" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{type?.description}</p>
              </div>
            </div>
            <ul className="space-y-1 ml-13">
              {type?.features?.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {errors?.helperType && (
          <p className="text-sm text-error">{errors?.helperType}</p>
        )}
      </div>
      <Select
        label="Preferred Location/Site"
        placeholder="Select location"
        options={locationOptions}
        value={formData?.preferredLocation || ''}
        onChange={(value) => onChange('preferredLocation', value)}
        description="Choose your preferred meeting location or select 'Any' for virtual sessions"
        error={errors?.preferredLocation}
      />
      <Select
        label="Session Format Preference"
        placeholder="Select format(s)"
        options={sessionFormatOptions}
        value={formData?.sessionFormats || []}
        onChange={(value) => onChange('sessionFormats', value)}
        multiple
        description="Select all formats you're comfortable with"
        required
        error={errors?.sessionFormats}
      />
      <div className="space-y-3">
        <label className="text-sm font-medium text-foreground">
          Additional Preferences
        </label>
        <Checkbox
          label="I prefer a helper of the same gender"
          checked={formData?.preferSameGender || false}
          onChange={(e) => onChange('preferSameGender', e?.target?.checked)}
          description="We'll prioritize matching based on gender preference"
        />
        <Checkbox
          label="I'm open to helpers of any gender"
          checked={formData?.openToAnyGender || false}
          onChange={(e) => onChange('openToAnyGender', e?.target?.checked)}
          description="Expands your matching options"
        />
        <Checkbox
          label="I prefer a helper with specific expertise in my concern area"
          checked={formData?.requireExpertise || false}
          onChange={(e) => onChange('requireExpertise', e?.target?.checked)}
          description="May increase wait time but ensures specialized support"
        />
      </div>
      <div className="p-4 bg-muted/50 rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium mb-1">Matching Algorithm</p>
            <p className="text-muted-foreground">
              Our system uses your preferences to find the best match while balancing availability and urgency. You can always update preferences after your first session.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelperPreferencesStep;